import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export const useGeneralLedgerStore = create(
  persist(
    (set, get) => ({
      // Chart of Accounts
      accounts: [
        { id: 1, accountNumber: '1000', name: 'Cash', type: 'Asset', subtype: 'Current Asset', balance: 45000, currency: 'USD', status: 'Active' },
        { id: 2, accountNumber: '1100', name: 'Accounts Receivable', type: 'Asset', subtype: 'Current Asset', balance: 125600, currency: 'USD', status: 'Active' },
        { id: 3, accountNumber: '1200', name: 'Inventory', type: 'Asset', subtype: 'Current Asset', balance: 89400, currency: 'USD', status: 'Active' },
        { id: 4, accountNumber: '1500', name: 'Equipment', type: 'Asset', subtype: 'Fixed Asset', balance: 85200, currency: 'USD', status: 'Active' },
        { id: 5, accountNumber: '2000', name: 'Accounts Payable', type: 'Liability', subtype: 'Current Liability', balance: 52300, currency: 'USD', status: 'Active' },
        { id: 6, accountNumber: '2100', name: 'Short-term Debt', type: 'Liability', subtype: 'Current Liability', balance: 25000, currency: 'USD', status: 'Active' },
        { id: 7, accountNumber: '2500', name: 'Long-term Debt', type: 'Liability', subtype: 'Long-term Liability', balance: 48100, currency: 'USD', status: 'Active' },
        { id: 8, accountNumber: '3000', name: 'Common Stock', type: 'Equity', subtype: 'Shareholders Equity', balance: 150000, currency: 'USD', status: 'Active' },
        { id: 9, accountNumber: '3100', name: 'Retained Earnings', type: 'Equity', subtype: 'Shareholders Equity', balance: 69800, currency: 'USD', status: 'Active' },
        { id: 10, accountNumber: '4000', name: 'Sales Revenue', type: 'Revenue', subtype: 'Operating Revenue', balance: 145600, currency: 'USD', status: 'Active' },
        { id: 11, accountNumber: '5000', name: 'Cost of Goods Sold', type: 'Expense', subtype: 'Cost of Revenue', balance: 52300, currency: 'USD', status: 'Active' },
        { id: 12, accountNumber: '5100', name: 'Salary Expense', type: 'Expense', subtype: 'Operating Expense', balance: 28500, currency: 'USD', status: 'Active' },
        { id: 13, accountNumber: '5200', name: 'Rent Expense', type: 'Expense', subtype: 'Operating Expense', balance: 12000, currency: 'USD', status: 'Active' },
        { id: 14, accountNumber: '5300', name: 'Utilities Expense', type: 'Expense', subtype: 'Operating Expense', balance: 5700, currency: 'USD', status: 'Active' },
      ],

      // Journal Entries
      journalEntries: [
        {
          id: 1,
          entryNumber: 'JE-2024-001',
          date: new Date(Date.now() - 0 * 24 * 60 * 60 * 1000),
          description: 'Monthly sales revenue recognition',
          lines: [
            { accountId: 1, accountNumber: '1000', debit: 5400, credit: 0 },
            { accountId: 10, accountNumber: '4000', debit: 0, credit: 5400 },
          ],
          totalDebit: 5400,
          totalCredit: 5400,
          status: 'Posted',
          postedBy: 'Admin',
          postedDate: new Date(),
        },
        {
          id: 2,
          entryNumber: 'JE-2024-002',
          date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          description: 'Payroll expense for March',
          lines: [
            { accountId: 12, accountNumber: '5100', debit: 8200, credit: 0 },
            { accountId: 6, accountNumber: '2100', debit: 0, credit: 8200 },
          ],
          totalDebit: 8200,
          totalCredit: 8200,
          status: 'Posted',
          postedBy: 'Admin',
          postedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        },
        {
          id: 3,
          entryNumber: 'JE-2024-003',
          date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          description: 'Equipment purchase',
          lines: [
            { accountId: 4, accountNumber: '1500', debit: 3100, credit: 0 },
            { accountId: 1, accountNumber: '1000', debit: 0, credit: 3100 },
          ],
          totalDebit: 3100,
          totalCredit: 3100,
          status: 'Draft',
          postedBy: null,
          postedDate: null,
        },
      ],

      // Account Reconciliation
      reconciliations: [
        { id: 1, accountId: 1, accountNumber: '1000', status: 'Matched', matchedAmount: 45000, variance: 0, lastReconciled: new Date() },
        { id: 2, accountId: 2, accountNumber: '1100', status: 'Matched', matchedAmount: 125600, variance: 0, lastReconciled: new Date() },
        { id: 3, accountId: 3, accountNumber: '1200', status: 'Pending', matchedAmount: 0, variance: 500, lastReconciled: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) },
      ],

      // Audit Trail
      auditTrail: [
        { id: 1, timestamp: new Date(Date.now() - 1 * 60 * 1000), action: 'Entry Posted', entryNumber: 'JE-2024-001', user: 'Admin', details: 'Posted monthly revenue entry' },
        { id: 2, timestamp: new Date(Date.now() - 5 * 60 * 1000), action: 'Account Created', accountNumber: '1200', user: 'Admin', details: 'Created Inventory account' },
        { id: 3, timestamp: new Date(Date.now() - 15 * 60 * 1000), action: 'Entry Modified', entryNumber: 'JE-2024-003', user: 'Admin', details: 'Updated equipment amount' },
      ],

      // Account Management Actions
      addAccount: (account) =>
        set((state) => ({
          accounts: [
            ...state.accounts,
            {
              ...account,
              id: Math.max(...state.accounts.map((a) => a.id), 0) + 1,
              balance: 0,
              status: 'Active',
            },
          ],
        })),

      updateAccount: (accountId, updates) =>
        set((state) => ({
          accounts: state.accounts.map((a) =>
            a.id === accountId ? { ...a, ...updates } : a
          ),
        })),

      deleteAccount: (accountId) =>
        set((state) => ({
          accounts: state.accounts.filter((a) => a.id !== accountId),
        })),

      // Journal Entry Actions
      addJournalEntry: (entry) =>
        set((state) => {
          const totalDebit = entry.lines.reduce((sum, line) => sum + line.debit, 0)
          const totalCredit = entry.lines.reduce((sum, line) => sum + line.credit, 0)
          return {
            journalEntries: [
              ...state.journalEntries,
              {
                ...entry,
                id: Math.max(...state.journalEntries.map((e) => e.id), 0) + 1,
                date: new Date(entry.date),
                totalDebit,
                totalCredit,
                status: 'Draft',
                postedBy: null,
                postedDate: null,
              },
            ],
          }
        }),

      updateJournalEntry: (entryId, updates) =>
        set((state) => ({
          journalEntries: state.journalEntries.map((e) =>
            e.id === entryId ? { ...e, ...updates } : e
          ),
        })),

      postJournalEntry: (entryId) =>
        set((state) => {
          const entry = state.journalEntries.find((e) => e.id === entryId)
          if (!entry) return state

          // Update account balances
          const updatedAccounts = [...state.accounts]
          entry.lines.forEach((line) => {
            const account = updatedAccounts.find((a) => a.id === line.accountId)
            if (account) {
              if (['Asset', 'Expense'].includes(account.type)) {
                account.balance += line.debit - line.credit
              } else {
                account.balance += line.credit - line.debit
              }
            }
          })

          return {
            accounts: updatedAccounts,
            journalEntries: state.journalEntries.map((e) =>
              e.id === entryId
                ? { ...e, status: 'Posted', postedBy: 'Admin', postedDate: new Date() }
                : e
            ),
            auditTrail: [
              ...state.auditTrail,
              {
                id: Math.max(...state.auditTrail.map((a) => a.id), 0) + 1,
                timestamp: new Date(),
                action: 'Entry Posted',
                entryNumber: entry.entryNumber,
                user: 'Admin',
                details: `Posted journal entry ${entry.entryNumber}`,
              },
            ],
          }
        }),

      deleteJournalEntry: (entryId) =>
        set((state) => ({
          journalEntries: state.journalEntries.filter((e) => e.id !== entryId),
        })),

      // Reconciliation Actions
      updateReconciliation: (reconciliationId, updates) =>
        set((state) => ({
          reconciliations: state.reconciliations.map((r) =>
            r.id === reconciliationId ? { ...r, ...updates } : r
          ),
        })),

      // Trial Balance Calculation
      getTrialBalance: () => {
        const state = get()
        return {
          accounts: state.accounts.map((account) => ({
            ...account,
            debitBalance: ['Asset', 'Expense'].includes(account.type) ? account.balance : 0,
            creditBalance: ['Liability', 'Equity', 'Revenue'].includes(account.type) ? account.balance : 0,
          })),
          totalDebits: state.accounts
            .filter((a) => ['Asset', 'Expense'].includes(a.type))
            .reduce((sum, a) => sum + a.balance, 0),
          totalCredits: state.accounts
            .filter((a) => ['Liability', 'Equity', 'Revenue'].includes(a.type))
            .reduce((sum, a) => sum + a.balance, 0),
          isBalanced: true,
        }
      },

      // Financial Statement Generation
      getIncomeStatement: () => {
        const state = get()
        const revenues = state.accounts.filter((a) => a.type === 'Revenue')
        const expenses = state.accounts.filter((a) => a.type === 'Expense')

        const totalRevenue = revenues.reduce((sum, a) => sum + a.balance, 0)
        const totalExpense = expenses.reduce((sum, a) => sum + a.balance, 0)
        const netIncome = totalRevenue - totalExpense

        return { revenues, expenses, totalRevenue, totalExpense, netIncome }
      },

      getBalanceSheet: () => {
        const state = get()
        const assets = state.accounts.filter((a) => a.type === 'Asset')
        const liabilities = state.accounts.filter((a) => a.type === 'Liability')
        const equity = state.accounts.filter((a) => a.type === 'Equity')

        const totalAssets = assets.reduce((sum, a) => sum + a.balance, 0)
        const totalLiabilities = liabilities.reduce((sum, a) => sum + a.balance, 0)
        const totalEquity = equity.reduce((sum, a) => sum + a.balance, 0)

        return { assets, liabilities, equity, totalAssets, totalLiabilities, totalEquity }
      },

      // Get Statistics
      getAccountingStats: () => {
        const state = get()
        const totalTransactions = state.journalEntries.length
        const postedEntries = state.journalEntries.filter((e) => e.status === 'Posted').length
        const draftEntries = state.journalEntries.filter((e) => e.status === 'Draft').length
        const reconciled = state.reconciliations.filter((r) => r.status === 'Matched').length
        const total = state.reconciliations.length
        const reconciliationRate = total > 0 ? Math.round((reconciled / total) * 100) : 0

        const bs = state.getBalanceSheet ? get().getBalanceSheet() : null
        const totalAssets = bs ? bs.totalAssets : 0

        return {
          totalTransactions,
          postedEntries,
          draftEntries,
          totalAssets: `$${totalAssets.toLocaleString()}`,
          reconciliationRate,
          accountCount: state.accounts.length,
        }
      },

      // Export data for reports
      getAllData: () => {
        const state = get()
        return {
          accounts: state.accounts,
          journalEntries: state.journalEntries,
          reconciliations: state.reconciliations,
          auditTrail: state.auditTrail,
        }
      },
    }),
    {
      name: 'general-ledger-store',
      getStorage: () => localStorage,
    }
  )
)
